import React, { useCallback, useEffect, useRef, useState } from "react";
import { languageByCode, screenTextMaximum, textLimit, type TranslatedText } from "@veyso/core";
import { ApiError, api, type LanguageLine, type LanguageView } from "../api.js";
import { editableLineIds, isInProgress, lineCount, lineUnit, textDirection } from "../languages/languages.js";
import { revoiceCount, textLength, textsEditable } from "./languageView.js";

/** How long typing pauses before an edit is saved. */
const SAVE_DELAY_MS = 600;

type Kind = "line" | "text";
type Edit = { kind: Kind; id: string; text: string };
const keyOf = (kind: Kind, id: string) => `${kind}:${id}`;

/** What a refused save says beside the line or text it was for. */
function refusalText(cause: unknown, unit: string): string {
  if (cause instanceof ApiError) {
    const details = cause.details as { budget?: number; maximum?: number } | undefined;
    if (cause.code === "line_too_long" && typeof details?.budget === "number") {
      return `Too long for the time it has. Keep it near ${details.budget} ${unit}.`;
    }
    if (cause.code === "text_too_long" && typeof details?.maximum === "number") {
      return `Longer than its place on screen holds: at most ${details.maximum} characters.`;
    }
    if (cause.code === "not_editable") return "This language can't be edited now.";
    if (cause.code === "text_marks_lost")
      return "Keep every {1} and every <a>…</a> as they are: the screen fills them in.";
    if (cause.code === "out_of_date") return "The English changed. Update this language before editing it.";
    if (cause.status === 409) return "This language changed meanwhile. Try the edit again.";
  }
  return "That didn't save. Try again in a moment.";
}

/**
 * A language's words in the editor's script column: each spoken line under
 * the English it came from, and — for the whole video — the words on screen.
 * Edits save as you type. What the API would refuse is not offered: in
 * review every line can change; once ready only the lines that run long,
 * and their edits are voiced again free.
 */
export const LanguageScript: React.FC<{
  projectId: string;
  language: LanguageView;
  /** A launch video's version, whose language this is; omitted for a product demo. */
  version?: string;
  /** Reads the languages again, after a save or a revoice. */
  onChanged: () => void;
}> = ({ projectId, language, version, onChanged }) => {
  const { code } = language;
  const dir = textDirection(code);
  const unit = lineUnit(code);
  const spec = languageByCode(code);
  const editable = editableLineIds(language);
  const textsOpen = textsEditable(language, Boolean(version));

  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [pending, setPending] = useState(0);
  const [revoicing, setRevoicing] = useState(false);
  const [revoiceError, setRevoiceError] = useState<string | null>(null);

  const timers = useRef(new Map<string, { timer: number; edit: Edit }>());
  // One save at a time: the API refuses a write that crosses another.
  const queue = useRef<Promise<unknown>>(Promise.resolve());
  const changed = useRef(onChanged);
  changed.current = onChanged;
  const alive = useRef(true);

  const save = useCallback(
    (edit: Edit) => {
      setPending((count) => count + 1);
      queue.current = queue.current
        .then(() =>
          api.editLanguage(
            projectId,
            code,
            edit.kind === "line"
              ? { lines: [{ id: edit.id, text: edit.text }] }
              : { texts: [{ id: edit.id, text: edit.text } as TranslatedText] },
            version,
          ),
        )
        .then(
          () => changed.current(),
          (cause: unknown) => {
            if (alive.current) setErrors((was) => ({ ...was, [keyOf(edit.kind, edit.id)]: refusalText(cause, unit) }));
            changed.current();
          },
        )
        .finally(() => {
          if (alive.current) setPending((count) => count - 1);
        });
    },
    [projectId, code, unit, version],
  );

  // Leaving the language (or the editor) saves what was still waiting.
  useEffect(() => {
    alive.current = true;
    const waiting = timers.current;
    return () => {
      alive.current = false;
      for (const { timer, edit } of waiting.values()) {
        window.clearTimeout(timer);
        save(edit);
      }
      waiting.clear();
    };
  }, [save]);

  // A draft the server now holds is no longer a draft.
  useEffect(() => {
    setDrafts((was) => {
      const next = { ...was };
      for (const line of language.lines)
        if (next[keyOf("line", line.id)] === line.text) delete next[keyOf("line", line.id)];
      for (const text of language.texts)
        if (next[keyOf("text", text.id)] === text.text) delete next[keyOf("text", text.id)];
      return Object.keys(next).length === Object.keys(was).length ? was : next;
    });
  }, [language.lines, language.texts]);

  const change = (kind: Kind, id: string, stored: string, text: string) => {
    const key = keyOf(kind, id);
    setDrafts((was) => ({ ...was, [key]: text }));
    setErrors((was) => {
      if (!(key in was)) return was;
      const next = { ...was };
      delete next[key];
      return next;
    });
    const waiting = timers.current.get(key);
    if (waiting) window.clearTimeout(waiting.timer);
    timers.current.delete(key);
    if (text.trim() === "" || text === stored) return;
    const edit = { kind, id, text };
    const timer = window.setTimeout(() => {
      timers.current.delete(key);
      save(edit);
    }, SAVE_DELAY_MS);
    timers.current.set(key, { timer, edit });
  };

  const toRevoice = revoiceCount(language);
  const revoice = async () => {
    setRevoicing(true);
    setRevoiceError(null);
    try {
      await queue.current;
      await api.voiceLanguage(projectId, code, version);
    } catch {
      if (alive.current) setRevoiceError("The lines couldn't be sent for voicing. Try again.");
    } finally {
      if (alive.current) setRevoicing(false);
      changed.current();
    }
  };

  const lineBlock = (line: LanguageLine, index: number) => {
    const key = keyOf("line", line.id);
    const text = drafts[key] ?? line.text;
    const count = lineCount(text, code);
    // A reworded line is judged by its words: the server keeps `runsLong` until it is revoiced.
    const long =
      (line.budget > 0 && count > line.budget) || (line.runsLong && !line.edited && drafts[key] === undefined);
    const canEdit = editable.has(line.id);
    const error = errors[key];
    const flagged = long || line.edited;
    return (
      <li
        key={line.id}
        className={`flex flex-col gap-1.5 rounded-[10px] px-3 py-2.5 ${
          flagged ? "border-2 border-ember-400 bg-ember-400/10" : "border border-[var(--edge)]"
        }`}
      >
        <p className="text-[12px] leading-[1.45] text-ink-300">{line.english}</p>
        {canEdit ? (
          <textarea
            className="input resize-y !px-2 !py-1.5 text-[13.5px] leading-[1.5]"
            rows={Math.max(2, Math.ceil(text.length / 34))}
            dir={dir}
            lang={code}
            aria-label={`Line ${index + 1} in ${language.name}`}
            value={text}
            onChange={(event) => change("line", line.id, line.text, event.target.value)}
          />
        ) : (
          <p className="text-[13.5px] leading-[1.5] text-ink-100" dir={dir} lang={code}>
            {text}
          </p>
        )}
        <span className={`text-[11px] ${long ? "font-semibold text-signal-warn" : "text-signal-ok"}`}>
          {count} of {line.budget} {unit} · {long ? "may run long" : "fits"}
          {line.edited && language.status === "ready" ? " · edited, voiced again free" : ""}
        </span>
        {error ? <span className="text-[11px] leading-snug text-signal-bad">{error}</span> : null}
      </li>
    );
  };

  const textBlock = (piece: TranslatedText, index: number) => {
    const key = keyOf("text", piece.id);
    const text = drafts[key] ?? piece.text;
    const maximum = screenTextMaximum(piece);
    const limit = spec ? textLimit(piece.english, spec, maximum) : maximum;
    const count = textLength(text);
    const long = count > limit;
    const error = errors[key];
    return (
      <li
        key={piece.id}
        className={`flex flex-col gap-1.5 rounded-[10px] px-3 py-2.5 ${
          long ? "border-2 border-ember-400 bg-ember-400/10" : "border border-[var(--edge)]"
        }`}
      >
        <p className="text-[12px] leading-[1.45] text-ink-300">
          <span className="mr-1.5 text-[10px] font-semibold uppercase tracking-wide text-ink-400">
            {piece.kind === "callout" ? "Callout" : "Card"}
          </span>
          {piece.english}
        </p>
        <textarea
          className="input resize-y !px-2 !py-1.5 text-[13.5px] leading-[1.5]"
          rows={Math.max(1, Math.ceil(text.length / 34))}
          dir={dir}
          lang={code}
          maxLength={maximum}
          aria-label={`On-screen text ${index + 1} in ${language.name}`}
          value={text}
          disabled={!textsOpen}
          onChange={(event) => change("text", piece.id, piece.text, event.target.value)}
        />
        <span className={`text-[11px] ${long ? "font-semibold text-signal-warn" : "text-ink-400"}`}>
          {count} of {limit} characters{long ? " · runs long" : ""}
        </span>
        {error ? <span className="text-[11px] leading-snug text-signal-bad">{error}</span> : null}
      </li>
    );
  };

  return (
    <div className="flex flex-col gap-3">
      {isInProgress(language.status) ? (
        <p className="rounded-[6px] border border-[var(--edge)] bg-bay-850 p-2.5 text-[11px] leading-relaxed text-ink-300">
          {language.status === "translating"
            ? `Translating ${language.nativeName}… this takes a minute or two.`
            : `Voicing ${language.nativeName}… this takes a minute or two.`}
        </p>
      ) : language.outOfDate ? (
        <p className="rounded-[6px] border border-[var(--edge)] bg-bay-850 p-2.5 text-[11px] leading-relaxed text-ink-300">
          The English changed after {language.nativeName} was made. Update it in Languages to edit its words.
        </p>
      ) : language.status === "ready" && editable.size === 0 ? (
        <p className="text-[11px] leading-relaxed text-ink-400">
          Every line fits its time. A line that runs long can be reworded here and voiced again, free.
        </p>
      ) : null}

      <ol className="flex flex-col gap-2" aria-label={`${language.nativeName} script`}>
        {language.lines.map(lineBlock)}
      </ol>

      {toRevoice > 0 ? (
        <div className="flex flex-col gap-1.5">
          <button
            type="button"
            className="btn btn-secondary min-h-[40px] border-ember-400 text-ember-600"
            disabled={revoicing || pending > 0}
            onClick={() => void revoice()}
          >
            {revoicing ? "Starting…" : `Revoice ${toRevoice} ${toRevoice === 1 ? "line" : "lines"} · free`}
          </button>
          {revoiceError ? <span className="text-[11px] leading-snug text-signal-bad">{revoiceError}</span> : null}
        </div>
      ) : null}

      {language.depth === "video" && language.texts.length > 0 ? (
        <section className="flex flex-col gap-2" aria-label={`${language.nativeName} words on screen`}>
          <span className="eyebrow mt-2 block">On screen</span>
          <ol className="flex flex-col gap-2">{language.texts.map(textBlock)}</ol>
        </section>
      ) : null}

      {pending > 0 ? <p className="text-[11px] text-ink-400">Saving…</p> : null}
    </div>
  );
};
